import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { differenceInMinutes } from "date-fns";
import { useTheme, Box, Typography, Tooltip, keyframes } from "@mui/material";
import { hasPermission } from "state/checkPermission";

const blinkBorder = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.8); border-color: rgba(211, 47, 47, 0.8); }
  50% { box-shadow: 0 0 6px 2px rgba(211, 47, 47, 0.5); border-color: rgba(211, 47, 47, 0.5); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.8); border-color: rgba(147, 61, 51, 0.8); }
`;

const TaskTimeline = ({ tasks = [], onEdit }) => {
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const canEdit = hasPermission(user?.permissions, "tatcontroller", "update");

  const maxTime = useMemo(
    () => Math.max(...tasks.map((t) => (t.startTime || 0) + (t.duration || 0)), 10),
    [tasks],
  );

  if (!tasks.length) return null;

  const rowHeight = 28;
  const totalHeight = tasks.length * rowHeight + 50;

  const getTaskVisual = (task) => {
    const now = new Date();
    const start = task.actualStartDate ? new Date(task.actualStartDate) : null;
    const end = task.actualEndDate ? new Date(task.actualEndDate) : null;

    let isOverdue = false;
    let overdueMinutes = 0;

    if (start) {
      const actualDuration = end
        ? differenceInMinutes(end, start)
        : differenceInMinutes(now, start);
      if (actualDuration > task.duration) {
        isOverdue = true;
        overdueMinutes = actualDuration - task.duration;
      }
    }

    const colors = {
      completed: "#4caf50",
      "in-progress": "#ff9800",
      pending: "#acacac",
    };

    return {
      background: colors[task.status] || "#9e9e9e",
      isOverdue,
      overdueMinutes,
    };
  };


  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: "bold", mb: 1, color: theme.palette.text.primary }}
      >
        Task Timeline Visualization
      </Typography>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: totalHeight,
          background: "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
          borderRadius: 1,
          p: 2,
          overflow: "hidden", 
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Timeline grid */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            display: "flex",
            opacity: 0.08,
          }}
        >
          {Array.from({ length: Math.ceil(maxTime / 10) }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                borderRight: "1.5px solid #999",
                height: "100%",
              }}
            />
          ))}
        </Box>

        {/* Task bars */}
        <Box sx={{ position: "relative", height: "100%" }}>
          {tasks.map((task, index) => {
            const startPercent = ((task.startTime || 0) / maxTime) * 100;
            const widthPercent = ((task.duration || 1) / maxTime) * 100;

            const { background, isOverdue, overdueMinutes } = getTaskVisual(task);

            const tooltipTitle = isOverdue
              ? `${task.taskName}: ${task.duration} min ⚠️ Overdue ${overdueMinutes} min`
              : `${task.taskName}: ${task.duration} min`;
           

            return (
              <Tooltip key={index} title={tooltipTitle} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    top: `${index * rowHeight}px`,
                    left: `${startPercent}%`,
                    width: `${widthPercent}%`,
                    height: "24px",
                    background,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    color: "#fff",
                    fontWeight: "bold",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    px: 1,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s ease",
                    cursor: "pointer",
                    animation: isOverdue ? `${blinkBorder} 1.5s infinite` : "none",
                    "&:hover": {
                      transform: "scale(1.05)",
                      zIndex: 2,
                    },
                  }}
                  onClick={() => {
                    if (!canEdit) return;
                    onEdit?.();
                  }}
                >
                  {task.taskName}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Time labels */}
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "#666",
            px: 2,
          }}
        >
          {Array.from({ length: Math.ceil(maxTime / 5) + 1 }).map((_, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{ color: "#666", fontSize: "10px" }}
            >
              {i * 5}m
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default TaskTimeline;